"use client";

import { useEffect, useState } from "react";
import { Ellipsis, CheckCircle } from "lucide-react";
import { useUsersStore } from "../../data/stores/loggerStore";
import { apiCall, useApiCall } from "../../data/useFetcher";
import PrimaryBtn, { LoadMore, MainPaginate } from "../partials/btn";
import moment from "moment";
import { TriggerModal } from "../modals/successmodal";
import { Status } from "../../utils/types";
import { useNavigate } from "react-router-dom";
import { FocalDataSystemModal } from "../modals/detailsmodal";

interface Contact {
  id: number;
  company: string;
  focalPerson: string;
  email: string;
  phone: string;
  status: Status;
  date: string;
}

function DataTable({
  usecase,
  setupStatus,
  search,
  isFocalPerson,
}: {
  usecase?: string;
  setupStatus?: string;
  search?: string;
  isFocalPerson?: boolean | string;
}) {
  const [datum, setDatum] = useState<any>({
      docs: [],
      hasNextPage: false,
    }),
    [updater, setUpdater] = useState<any>(null),
    { getDynamicLogger, getSearchLogger } = useUsersStore(),
    { onSubmitAPI, loading: isLoading } = useApiCall();

  const onSubmit = async (handleDeleteContact?: any) => {
    let data: any = {
      members: updater?.data?.map((ic: any) => {
        return { organization: ic?._id };
      }),
      type: updater?.isOpen,
    };
    if (isFocalPerson) {
      data.mainOrganization = isFocalPerson;
      data.usecase = "focal person";
    }
    await onSubmitAPI({
      url: `/api/v1/auth/user/manage-users`,
      type: "put",
      data,
      toggleModal: () => {
        // handleModal();
        if (handleDeleteContact) handleDeleteContact(updater?.data?.[0]);
        apiCall({
          type: "get",
          url: `/api/v1/auth/user/manage-users?privilege=${
            isFocalPerson ? "focal person" : "system owner"
          }${
            usecase === "newly-uploaded"
              ? `&setupStatus=Pending Acceptance`
              : usecase === "view-all" && setupStatus
              ? `&setupStatus=${setupStatus}`
              : ""
          }${search ? `&search=${search}` : ""}${
            isFocalPerson ? `&organization=${isFocalPerson}` : ""
          }`,
          getter: (d: any) =>
            search
              ? getSearchLogger(d)
              : getDynamicLogger(
                  d,
                  usecase === "newly-uploaded"
                    ? `newlyUploadedData${isFocalPerson ? "FocalPerson" : ""}`
                    : `allData${isFocalPerson ? "FocalPerson" : ""}`
                ),
        });
        setUpdater(null);
      },
    });
  };

  return (
    <div className="px-5">
      {datum?.docs?.some?.((ic) => ic?.isChecked) && (
        <div className={`flex justify-end items-center gap-3 my-4`}>
          <PrimaryBtn
            loadingColor="blue"
            text="Resend Invitation"
            className={
              "w-full text-left p-3 hover:bg-gray-100 text-blue-500 rounded-md flex items-center gap-3 border border-dashed border-blue-500 relative"
            }
            onClick={() =>
              setUpdater({
                isOpen: "resend",
                data: datum?.docs?.filter((iz) => iz?.isChecked),
                title: "Resend Invitation",
              })
            }
          >
            Resend Invitation
          </PrimaryBtn>
          <PrimaryBtn
            loadingColor="red"
            text="Delete Account"
            className={
              "w-full text-left p-3 hover:bg-gray-100 text-red-500 rounded-md flex items-center gap-3 border border-dashed border-red-500 relative"
            }
            onClick={() =>
              setUpdater({
                isOpen: "delete",
                data: datum?.docs?.filter((iz) => iz?.isChecked),
                title: "Delete Account",
              })
            }
          >
            Delete Account
          </PrimaryBtn>
        </div>
      )}
      <table className="w-full">
        <thead>
          <tr className="">
            <th className="p-3 text-left font-normal text-gray-600">
              <CheckAllboxes datum={datum} setDatum={setDatum} />
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              {isFocalPerson ? "Department" : `Company`}
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085] hidden">
              Focal Person
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Email address
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085] hidden">
              Phone
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              Status <span className="inline-block ml-1">↓</span>
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]">
              {isFocalPerson ? "Date Completed" : `Date`}
            </th>
            <th className="p-3 text-xs text-left font-medium text-[#667085]"></th>
          </tr>
        </thead>
        <InviteTable
          usecase={usecase}
          datum={datum}
          setDatum={setDatum}
          updater={updater}
          setUpdater={setUpdater}
          onSubmit={onSubmit}
          isLoading={isLoading}
          setupStatus={setupStatus}
          search={search}
          isFocalPerson={isFocalPerson}
        />
      </table>
    </div>
  );
}

export default DataTable;

export const InviteTable = ({
  usecase,
  datum,
  setDatum,
  updater,
  setUpdater,
  onSubmit,
  isLoading,
  setupStatus,
  search,
  isFocalPerson,
}: {
  usecase?: string;
  setupStatus?: string;
  datum: any;
  setDatum: React.Dispatch<any>;
  updater: any;
  setUpdater: React.Dispatch<any>;
  onSubmit: (h?: any) => Promise<void>;
  isLoading: string | boolean;
  search?: string;
  isFocalPerson?: string | boolean;
}) => {
  const {
      mainSearch,
      isFound,
      newlyUploadedData,
      allData,
      allDataFocalPerson,
      newlyUploadedDataFocalPerson,
    }: any = useUsersStore(),
    { getDynamicLogger, resetLoggerSearch, getSearchLogger, getSearch } =
      useUsersStore(),
    [loading, setLoading] = useState<string | boolean>(false),
    navigate = useNavigate(),
    [detail, setDetail] = useState<any>("");

  useEffect(() => {
    resetLoggerSearch();
  }, []);

  useEffect(() => {
    if (isFound) setDatum(mainSearch);
    else
      setDatum(
        isFocalPerson
          ? usecase === "newly-uploaded"
            ? newlyUploadedDataFocalPerson
            : allDataFocalPerson
          : usecase === "newly-uploaded"
          ? newlyUploadedData
          : allData
      );
  }, [
    isFound,
    mainSearch,
    newlyUploadedData,
    usecase,
    allData,
    isFocalPerson,
    newlyUploadedDataFocalPerson,
    allDataFocalPerson,
  ]);

  useEffect(() => {
    if (search) getSearch(search);
    apiCall({
      type: "get",
      url: `/api/v1/auth/user/manage-users?privilege=${
        isFocalPerson ? "focal person" : "system owner"
      }${
        usecase === "newly-uploaded"
          ? `&setupStatus=Pending Acceptance`
          : usecase === "view-all" && setupStatus
          ? `&setupStatus=${setupStatus}`
          : ""
      }${search ? `&search=${search}` : ""}${
        isFocalPerson ? `&organization=${isFocalPerson}` : ""
      }`,
      getter: (d: any) =>
        search
          ? getSearchLogger(d)
          : getDynamicLogger(
              d,
              usecase === "newly-uploaded"
                ? `newlyUploadedData${isFocalPerson ? "FocalPerson" : ""}`
                : `allData${isFocalPerson ? "FocalPerson" : ""}`
            ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usecase, setupStatus, search, isFocalPerson]);

  useEffect(() => {
    if (search) {
      document.getElementById("Search").addEventListener("search", () => {
        resetLoggerSearch();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleLoadMore = async () => {
    setLoading("loadmore");
    await apiCall({
      type: "get",
      url: `/api/v1/auth/user/manage-users?privilege=${
        isFocalPerson ? "focal person" : "system owner"
      }
          &limit=${datum?.limit * datum?.nextPage}${
        usecase === "newly-uploaded"
          ? `&setupStatus=Pending Acceptance`
          : usecase === "view-all" && setupStatus
          ? `&setupStatus=${setupStatus}`
          : ""
      }${search ? `&search=${search}` : ""}${
        isFocalPerson ? `&organization=${isFocalPerson}` : ""
      }`,
      getter: (d: any) =>
        search
          ? getSearchLogger(d)
          : getDynamicLogger(
              d,
              usecase === "newly-uploaded"
                ? `newlyUploadedData${isFocalPerson ? "FocalPerson" : ""}`
                : `allData${isFocalPerson ? "FocalPerson" : ""}`
            ),
    });
    setLoading(false);
  };

  const [trashActive, setTrashActive] = useState<number | null>(null);
  const [editActive, setEditActive] = useState<number | null>(null);

  const getStatusColor = (status: Status) => {
    switch (status) {
      case "Pending Acceptance":
        return "text-purple-600";
      case "In Progress":
        return "text-amber-600";
      case "Assessment Complete":
        return "text-green-600";
      case "Invite Expired":
        return "text-red-600";
      default:
        return "";
    }
  };

  const [itemOffset, setItemOffset] = useState(0);
  const [range] = useState(10),
    [page, setPage] = useState(1);
  const endOffset = itemOffset + range;

  // if (!datum && !data) return <PageLoader />;
  if (!datum) return;

  const currentItems = datum?.docs?.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(datum?.docs?.length / range);

  const handlePageClick = (event: any) => {
    const newOffset = (event.selected * range) % datum?.docs?.length;
    setItemOffset(newOffset);
    setPage(1 + event?.selected);
  };

  const handleDeleteContact = (id: number) => {
    // setContacts(contacts.filter(contact => contact.id !== id));
    setEditActive(null);
    setTrashActive(null);
  };

  return (
    <>
      <tbody>
        {currentItems?.map((contact: any, i: number) => (
          <tr
            style={{
              background: i % 2 === 0 ? "#F9FAFB" : "transparent",
            }}
            key={contact._id}
            className="h-20"
          >
            <td className="p-3">
              <Checkbox item={contact} datum={datum} setDatum={setDatum} />
            </td>
            <td className="p-3 text-sm text-sec font-medium">
              {contact?.user?.organizationName || contact?.user?.departmentName}
            </td>
            <td className="p-3 text-sm text-sec font-medium hidden">
              {contact.focalPerson}
            </td>
            <td className="p-3 text-sm text-[#667085] font-mediu">
              {contact?.user?.email}
            </td>
            <td className="p-3 text-sm whitespace-nowrap text-[#667085] font-medium hidden">
              {contact?.user?.telephone}
            </td>
            <td className="p-3 font-medium">
              <div
                className={`inline-flex items-center px-5 h-8 rounded-2xl ${
                  contact.setupStatus === "Invite Expired"
                    ? "bg-[#FEF3F2]"
                    : contact.setupStatus === "Assessment Complete"
                    ? "bg-[#ECFDF3]"
                    : contact.setupStatus === "Pending Acceptance"
                    ? "bg-[#f1E3FD]"
                    : "bg-[#FFFAEB]"
                }`}
              >
                <span
                  className={`text-xs rounded-full mr-2 ${
                    contact.setupStatus === "Invite Expired"
                      ? "bg-red-500"
                      : contact.setupStatus === "Assessment Complete"
                      ? "bg-green-500"
                      : contact.setupStatus === "Pending Acceptance"
                      ? "bg-purple-500"
                      : "bg-amber-500"
                  }`}
                ></span>
                <span
                  style={{
                    fontSize: "12px",
                    whiteSpace: "nowrap",
                  }}
                  className={getStatusColor(contact.setupStatus)}
                >
                  {contact.setupStatus}
                </span>
              </div>
            </td>

            <td className="p-3 text-sm font-medium text-[#667085]">
              {isFocalPerson ? (
                <>
                  {contact?.dateCompleted
                    ? moment(contact?.dateCompleted)?.format("DD/MM/YY")
                    : null}
                </>
              ) : (
                moment(contact?.createdAt)?.format("DD/MM/YY")
              )}
            </td>
            <td className="p-3 flex justify-center items-center h-20 space-x-4">
              {/* <div className="relative">
                  <button
                    className="text-[#667085] hover:text-gray-700"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTrashActive(trashActive === i ? null : i);
                      setEditActive(null);
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                  {trashActive === i && (
                    <div className="absolute top-8 right-0 z-10 w-64 bg-white rounded-lg border border-gray-200 shadow-md p-3">
                      <div className="py-2 px-1">
                        <h3 className="text-sm text-gray-500 mb-2">Action</h3>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log("View", contact);
                            setTrashActive(null);
                          }}
                          className="w-full text-left p-3 hover:bg-gray-100 text-gray-700 rounded-md flex items-center gap-3 mb-2 bg-gray-50"
                        >
                          <div className="rounded-full p-1 bg-gray-200">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 6L9 17L4 12"
                                stroke="#4B5563"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-base">View Profile</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log("Delete", contact);
                            setTrashActive(null);
                          }}
                          className="w-full text-left p-3 hover:bg-gray-100 text-red-500 rounded-md flex items-center gap-3 border border-dashed border-purple-300 relative"
                        >
                          <div className="rounded-md p-1 border border-red-500">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3 6H5H21"
                                stroke="#EF4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                                stroke="#EF4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-base">Remove account</span>
                          <div className="absolute -bottom-2 -left-1 bg-purple-500 text-white text-xs px-1 rounded">
                            20 20
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div> */}
              <div className="relative">
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditActive(editActive === i ? null : i);
                    setTrashActive(null);
                  }}
                >
                  <Ellipsis size={18} />
                </button>
                {editActive === i && (
                  <div className="absolute top-8 right-0 z-10 w-64 bg-white rounded-lg border border-gray-200 shadow-md p-3">
                    <div className="py-2 px-1">
                      <button
                        // onClick={e => {
                        // 	console.log("View", contact);
                        // 	setEditActive(null);
                        // }}
                        onClick={(e) => {
                          e?.stopPropagation();
                          navigate(`${contact?.slug || i}`, {
                            state: contact,
                          });
                        }}
                        className="w-full text-left p-3 hover:bg-gray-100 text-gray-700 rounded-md hidden items-center gap-3 bg-gray-50"
                      >
                        <div className="rounded-full p-1 bg-gray-200">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="#4B5563"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="text-base">View Profile</span>
                      </button>
                      <button
                        // onClick={e => {
                        // 	console.log("View", contact);
                        // 	setEditActive(null);
                        // }}
                        onClick={(e) => {
                          e?.stopPropagation();
                          if (isFocalPerson) {
                            setDetail(contact);
                          } else
                            navigate(`focal-person`, {
                              state: contact,
                            });
                          setEditActive(null);
                        }}
                        className={`w-full text-left p-3 hover:bg-gray-100 text-gray-700 rounded-md flex items-center gap-3 bg-gray-50`}
                      >
                        <div className="rounded-full p-1 bg-gray-200">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="#4B5563"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <span className="text-base">
                          View {isFocalPerson ? "Data System" : `Department`}
                        </span>
                      </button>
                      {/* <button className="w-full text-left p-3 hover:bg-gray-100 text-red-500 rounded-md flex items-center gap-3 border border-dashed border-purple-300 relative">
												<div className="rounded-md p-1 border border-red-500">
													<svg
														width="20"
														height="20"
														viewBox="0 0 24 24"
														fill="none"
														xmlns="http://www.w3.org/2000/svg">
														<path
															d="M3 6H5H21"
															stroke="#EF4444"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
														<path
															d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
															stroke="#EF4444"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
														/>
													</svg>
												</div>
												<span className="text-base">Remove account</span>
											</button> */}
                      <PrimaryBtn
                        // onClick={async e => {
                        // 	e.stopPropagation();
                        // 	// handleDeleteContact(contact.id);
                        // 	await onSubmit({ organization: contact?._id });
                        // }}
                        onClick={async (e) => {
                          e?.stopPropagation();
                          setUpdater({
                            isOpen: "resend",
                            data: [contact],
                            title: "Resend Invitation",
                          });
                          setEditActive(null);
                        }}
                        // loadingColor="red"
                        text="Delete"
                        className={`w-full text-left p-3 hover:bg-gray-100 text-gray-500 rounded-md flex items-center gap-3 border border-dashed border-purple-300 relative my-2`}
                        loading={!!isLoading}
                      >
                        <>
                          <div className="rounded-md p-1 border border-gray-500">
                            <CheckCircle className="text-gray-500" />
                          </div>
                          <span className="text-base">Resend Invitation</span>
                        </>
                      </PrimaryBtn>
                      <PrimaryBtn
                        // onClick={async e => {
                        // 	e.stopPropagation();
                        // 	// handleDeleteContact(contact.id);
                        // 	await onSubmit({ organization: contact?._id });
                        // }}
                        onClick={async (e) => {
                          e?.stopPropagation();
                          setUpdater({
                            isOpen: "delete",
                            data: [contact],
                            title: "Delete Account",
                          });
                          setEditActive(null);
                        }}
                        loadingColor="red"
                        text="Delete"
                        className={`w-full text-left p-3 hover:bg-gray-100 text-red-500 rounded-md flex items-center gap-3 border border-dashed border-purple-300 relative`}
                        loading={!!isLoading}
                      >
                        <>
                          <div className="rounded-md p-1 border border-red-500">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3 6H5H21"
                                stroke="#EF4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z"
                                stroke="#EF4444"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <span className="text-base">Delete account</span>
                        </>
                      </PrimaryBtn>
                    </div>
                  </div>
                )}
              </div>
            </td>
            {/* <td className="p-3 flex justify-center bg-[#F9FAFB] items-center h-20 space-x-2">
                <div className="relative">
                  <button className="text-[#667085] hover:text-gray-700">
                    <Trash2 size={18} />
                  </button>
                </div>
                <div className="relative">
                  <button className="text-gray-500 hover:text-gray-700">
                    <Edit2
                      // onClick={() => navigate(`/requests/${i}`)}
                      size={18}
                    />
                  </button>
                </div>
              </td> */}
          </tr>
        ))}
      </tbody>
      <div className="mt-4 flex justify-center">
        <LoadMore
          next={page === pageCount && datum?.hasNextPage}
          loading={loading === "loadmore"}
          handleLoadMore={handleLoadMore}
        />
      </div>
      {pageCount > 1 && (
        <MainPaginate pageCount={pageCount} handlePageClick={handlePageClick} />
      )}
      {updater?.isOpen && (
        <TriggerModal
          title={updater?.title || `${updater?.isOpen?.toUpperCase()} Account`}
          desc={
            updater?.isOpen === "delete"
              ? `Action done cannot be reversible, Proceed to to delete?`
              : `Proceed to update selected invite?`
          }
          onClose={() => setUpdater(null)}
          onClick={() => {
            if (["delete", "resend"]?.includes(updater?.isOpen)) {
              onSubmit(handleDeleteContact);
            }
          }}
          loading={!!isLoading}
        />
      )}
      {detail && (
        <FocalDataSystemModal
          handleClose={() => setDetail("")}
          detail={detail}
        />
      )}
    </>
  );
};

export const CheckAllboxes = ({ datum, setDatum }) => {
  return (
    <>
      <input
        type="checkbox"
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600 main-border"
        onChange={(e) => {
          let dd = datum?.docs;
          dd = dd?.map((ic) => {
            let dc = ic;
            return { ...dc, isChecked: e?.target?.checked };
          });
          setDatum({ ...datum, docs: dd });
        }}
        checked={
          datum?.docs?.length > 0 && datum?.docs?.every((it) => it?.isChecked)
        }
      />
    </>
  );
};

export const Checkbox = ({ datum, setDatum, item }) => {
  return (
    <>
      <input
        id="checkbox-table-search-1"
        type="checkbox"
        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
        onChange={(e) => {
          let dd = datum?.docs;
          let current = item;
          if (e?.target?.checked) {
            current = {
              ...current,
              isChecked: true,
            };
          } else {
            current = {
              ...current,
              isChecked: false,
            };
          }
          dd = dd?.map((ic) => (ic?._id === item?._id ? current : ic));
          setDatum({ ...datum, docs: dd });
        }}
        checked={item?.isChecked}
      />
    </>
  );
};
